
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';
import { useToast } from '@/hooks/use-toast';
import { saveReportRecord } from '@/utils/reportUtils';
import { markCertificatesAsReported } from '@/utils/reports/verificationUtils';

export interface ReportDeadline {
  id?: string;
  state: string;
  year: number;
  quarter?: number;
  month?: number;
  dueDate: Date;
  status: 'upcoming' | 'due' | 'overdue' | 'completed';
}

export function useReportDeadlines() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { sendRetentionNotification } = useAdminNotifications();

  // Query to get all upcoming deadlines
  const { data: deadlines, isLoading: isLoadingDeadlines, refetch } = useQuery({
    queryKey: ['reportDeadlines'],
    queryFn: async () => {
      try {
        // Check if we have upcoming deadlines by calling our edge function
        const { data, error } = await supabase.functions.invoke('check-report-deadlines');
        
        if (error) throw error;
        
        return data.deadlines || [];
      } catch (err: any) {
        console.error('Failed to fetch report deadlines:', err);
        return [];
      }
    },
  });

  // Mark report as complete
  const markReportCompleteMutation = useMutation({
    mutationFn: async ({ 
      state, 
      year, 
      quarter, 
      reportData 
    }: { 
      state: string; 
      year: number; 
      quarter?: number;
      reportData: any;
    }) => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Create report record
        const { data, error } = await supabase
          .from('certificate_reports')
          .insert({
            state,
            quarter: quarter || null,
            year,
            report_data: reportData,
            status: 'completed',
            format: 'json',
            generated_at: new Date().toISOString(),
            generated_by: (await supabase.auth.getUser()).data.user?.id
          })
          .select()
          .single();
        
        if (error) throw error;
        
        // Send notification about completed report
        sendRetentionNotification(
          `${state} Report Completed: ${quarter ? `Q${quarter} ${year}` : year}`,
          `The ${state} ${quarter ? `Q${quarter} ${year}` : year} report has been completed and submitted.`,
          {
            state,
            reportingPeriod: quarter ? `Q${quarter} ${year}` : `${year}`,
            completedAt: data.generated_at,
            reportId: data.id
          }
        );
        
        toast({
          title: "Report Completed",
          description: `The ${state} ${quarter ? `Q${quarter} ${year}` : year} report has been marked as completed.`,
        });
        
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to mark report as complete';
        setError(err);
        toast({
          variant: 'destructive',
          title: "Error",
          description: errorMessage,
        });
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      refetch();
    }
  });

  // Generate report for specific deadline
  const generateReportMutation = useMutation({
    mutationFn: async ({ 
      state, 
      year, 
      quarter 
    }: { 
      state: string; 
      year: number; 
      quarter?: number;
    }) => {
      try {
        setIsLoading(true);
        setError(null);
        
        const startDate = new Date(year, quarter ? (quarter - 1) * 3 : 0, 1);
        const endDate = new Date(year, quarter ? quarter * 3 : 12, 0);
        
        // Format dates for the query
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Query certificates for the report period
        const { data, error } = await supabase
          .from('certificate_number_inventory')
          .select(`
            *,
            certificates:used_by (
              id,
              user_id,
              issued_at,
              certificate_number,
              is_revoked,
              profiles:user_id (
                full_name,
                email,
                driver_license_number,
                driver_license_state,
                county
              )
            )
          `)
          .eq('state', state)
          .gte('assigned_at', formattedStartDate)
          .lte('assigned_at', formattedEndDate)
          .order('assigned_at', { ascending: true });
        
        if (error) throw error;
        
        // Process data into report format
        const reportData = {
          state,
          reportingPeriod: quarter ? `Q${quarter} ${year}` : `${year}`,
          generatedAt: new Date().toISOString(),
          certificatesCount: data.length,
          certificates: data.map(cert => ({
            certificateNumber: cert.certificate_number,
            issuedAt: cert.assigned_at,
            studentName: (cert as any).certificates?.profiles?.full_name || 'N/A',
            driverLicense: (cert as any).certificates?.profiles?.driver_license_number || 'N/A',
            driverLicenseState: (cert as any).certificates?.profiles?.driver_license_state || 'N/A',
            county: (cert as any).certificates?.profiles?.county || 'N/A',
            isRevoked: (cert as any).certificates?.is_revoked || false
          }))
        };

        // Extract certificate numbers for updating
        const certificateNumbers = data.map(cert => cert.certificate_number);
        
        // Save the report to the database
        const { success, error: saveError, reportId } = await saveReportRecord(
          year,
          quarter || 1,
          state,
          JSON.stringify(reportData),
          'json'
        );

        if (!success || saveError) {
          throw new Error(saveError || 'Failed to save report record');
        }

        // Mark certificates as reported
        if (certificateNumbers.length > 0 && reportId) {
          await markCertificatesAsReported(certificateNumbers, reportId);
        }
        
        // Send notification
        sendRetentionNotification(
          `${state} Report Generated: ${quarter ? `Q${quarter} ${year}` : year}`,
          `A new report for ${state} ${quarter ? `Q${quarter} ${year}` : year} has been generated with ${data.length} certificates.`,
          {
            state,
            reportingPeriod: quarter ? `Q${quarter} ${year}` : `${year}`,
            certificates: data.length,
            reportId
          }
        );

        return { reportData, reportId };
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to generate report';
        setError(err);
        toast({
          variant: 'destructive',
          title: "Error",
          description: errorMessage,
        });
        throw err;
      } finally {
        setIsLoading(false);
      }
    }
  });

  return {
    deadlines,
    isLoading: isLoadingDeadlines || isLoading,
    error,
    markReportComplete: markReportCompleteMutation.mutate,
    generateReport: generateReportMutation.mutate,
    refetchDeadlines: refetch,
  };
}
